import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { OrganizationLevel } from 'src/app/features/organization-search/organization-search.component';
import { clientServiceEndpoints } from 'src/app/helpers/urls';
import { ClientDto } from 'src/app/models/client/client-dto';
import { ResponsibleType } from 'src/app/models/client/client-responsible-dto';
import {
  ClientFilter,
  ClientSearchFilterDto,
  ConfigurationFilter,
  FilterOptions,
  ResponsibleFilter
} from 'src/app/models/client/client-search-filter-dto';
import { OrganizationDto } from 'src/app/models/organization.dto';
import { SearchListConfig } from 'src/app/models/search-list-config';
import { ClientService } from 'src/app/services/api/client-api';
import { UserMicroservice } from 'src/app/services/api/user-api';
import { OrganizationSearchComponent } from '../../../../features/organization-search/organization-search.component';
import { SearchableListMicroComponent } from '../../../../features/searchable-list-micro/searchable-list-micro.component';
import { RulePropertyEditEvent } from '../../model/rule-property-edit-event';
import { NOTIFICATIONS_SETUP_CLIENT_IDS_MODAL_DATA } from './notifications-setup-client-ids-modal.modal';

@Component({
  templateUrl: './notifications-setup-client-ids-modal.component.html',
  styleUrl: './notifications-setup-client-ids-modal.component.scss',
  imports: [
    MultiSelectComponent,
    OrganizationSearchComponent,
    SearchableListMicroComponent
  ]
})
export class NotificationsSetupClientIdsModalComponent implements OnInit {
  protected readonly activeModal = inject(NgbActiveModal);
  protected readonly inputData = inject(
    NOTIFICATIONS_SETUP_CLIENT_IDS_MODAL_DATA
  );
  private readonly clientService = inject(ClientService);
  private readonly userService = inject(UserMicroservice);
  private readonly destroyRef = inject(DestroyRef);

  protected clients: ClientDto[];

  clientConfig: SearchListConfig;
  clientsUrl: string = clientServiceEndpoints.getClientsEndpoint;

  clientRelationState: ClientRelationState[] = [];
  responsibles: ResponsibleTypes[] = [];
  enabledOrganizationLevels: OrganizationLevel[] = [
    OrganizationLevel.Level4,
    OrganizationLevel.Level5
  ];

  constructor() {
    this.clientConfig = new SearchListConfig();
    this.clientConfig.placeholder = 'Filter here by clients';
    this.clientConfig.label = 'Client';
    this.clientConfig.key = 'clients';
    this.clientConfig.virtual = true;
    this.clientConfig.mode = 'list';
    this.clientConfig.onlyEnabled = true;
  }

  ngOnInit(): void {
    this.clients = [...this.inputData.clients];
    this.initResponsibles();
  }

  changeFilter(selectedOrganizations: OrganizationDto[]): void {
    const clientIdsToRemove: number[] = [];
    const removedOrgClients = this.clientRelationState.filter(
      orgC =>
        orgC.responsibleType == null &&
        !selectedOrganizations.map(s => s.id).includes(orgC.organizationId)
    );

    if (removedOrgClients?.length > 0) {
      clientIdsToRemove.push(...removedOrgClients.flatMap(x => x.clientIds));
      this.clientRelationState = this.clientRelationState.filter(c =>
        removedOrgClients.some(r => r != c)
      );
    }

    this.clients = this.clients.filter(c => !clientIdsToRemove.includes(c.id));

    if (selectedOrganizations?.length > 0) {
      this.clientService
        .getBaseClients$(
          null,
          null,
          selectedOrganizations.map(o => o.id)
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(cl => {
          for (const org of selectedOrganizations) {
            this.clientRelationState.push(<ClientRelationState>{
              organizationId: org.id,
              clientIds: cl.map(c => c.id)
            });
          }
          const clients = cl.filter(
            c => !this.clients.map(cc => cc.id).includes(c.id)
          );
          this.clients.push(...clients);
        });
    }
  }

  valueChange(selectedResponsibles: ResponsibleTypes[]): void {
    const clientIdsToRemove: number[] = [];
    const removedResponsibleClients = this.clientRelationState.filter(
      cr =>
        cr.organizationId == null &&
        !selectedResponsibles.map(s => s.id).includes(cr.responsibleType)
    );

    if (removedResponsibleClients?.length > 0) {
      clientIdsToRemove.push(
        ...removedResponsibleClients.flatMap(x => x.clientIds)
      );
      this.clientRelationState = this.clientRelationState.filter(c =>
        removedResponsibleClients.some(r => r != c)
      );
    }

    this.clients = this.clients.filter(c => !clientIdsToRemove.includes(c.id));

    if (selectedResponsibles?.length > 0) {
      const lastItem = selectedResponsibles[selectedResponsibles.length - 1];
      this.getClientsByResponsible(lastItem);
    }
  }

  save(): void {
    const event = new RulePropertyEditEvent<ClientDto[]>();
    event.notificationRuleId = this.inputData.notificationRuleId;
    event.value = this.clients;

    this.activeModal.close({
      saveAction: event
    });
  }

  close(): void {
    this.activeModal.dismiss();
  }

  persistedClients(clients: ClientDto[]): void {
    this.clientConfig.data = clients;
  }

  async getClientIds(event): Promise<void> {
    if (event != null && event.length > 0) {
      const result = await this.getDataAsync(event);
      this.clientsSelected(result);
    }
  }

  private clientsSelected(clients: ClientDto[]): void {
    if (this.clientConfig.data.length == 0) {
      this.clientConfig.data.push(...clients);
      return;
    }
    const filteredClients = clients.filter(
      client => !this.clientConfig.data.some(config => config.id === client.id)
    );
    this.clientConfig.data.push(...filteredClients);
  }

  private async getDataAsync(ids: number[]): Promise<ClientDto[]> {
    const result: ClientDto[] = [];
    if (ids) {
      let batchIds: number[] = [];
      for (const id of ids) {
        batchIds.push(id);
        if (batchIds.length % 100 == 0) {
          const clients =
            await this.clientService.getBaseClientsAsync(batchIds);
          if (clients?.length) {
            result.push(...clients);
          }
          batchIds = [];
        }
      }
      if (batchIds.length > 0) {
        const clients = await this.clientService.getBaseClientsAsync(batchIds);
        if (clients?.length) {
          result.push(...clients);
        }
      }
    }
    return result;
  }

  private initResponsibles(): void {
    this.responsibles = [
      {
        id: ResponsibleType.CustomerResponsible,
        name: 'Customer responsible'
      },
      {
        id: ResponsibleType.AccountingResponsible,
        name: 'Accounting responsible'
      },
      {
        id: ResponsibleType.PayrollResponsible,
        name: 'Payroll responsible'
      },
      {
        id: ResponsibleType.YearEndResponsible,
        name: 'YearEnd responsible'
      },
      {
        id: ResponsibleType.CharteredAccountantResponsible,
        name: 'Chartered accountant responsible'
      },
      {
        id: ResponsibleType.AccountingBackupResponsible,
        name: 'Accounting backup responsible'
      },
      {
        id: ResponsibleType.AccountingOnboardingResponsible,
        name: 'Accounting onboarding responsible'
      },
      {
        id: ResponsibleType.PayrollBackupResponsible,
        name: 'Payroll backup responsible'
      },
      {
        id: ResponsibleType.PayrollOnboardingResponsible,
        name: 'Payroll onboarding responsible'
      }
    ];
  }

  private getClientsByResponsible(selectedResponsible: ResponsibleTypes): void {
    const filter: ClientSearchFilterDto = {
      options: <FilterOptions>{
        pageSize: 100
      },
      client: <ClientFilter>{
        responsible: this.createFilter(selectedResponsible)
      },
      configuration: new ConfigurationFilter()
    };
    this.clientService
      .getClients$(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const clientsToAdd = result.clients.filter(
          c => !this.clients.map(cc => cc.id).includes(c.id)
        );
        this.clientRelationState.push(<ClientRelationState>{
          clientIds: clientsToAdd.map(c => c.id),
          responsibleType: selectedResponsible.id
        });
        this.clients.push(...clientsToAdd);
      });
  }

  private createFilter(
    selectedResponsible: ResponsibleTypes
  ): ResponsibleFilter {
    const userId = this.userService.getSelfId();
    const responsibleFilter = <ResponsibleFilter>{
      customer:
        selectedResponsible.id == ResponsibleType.CustomerResponsible
          ? [userId]
          : [],
      accounting:
        selectedResponsible.id == ResponsibleType.AccountingResponsible
          ? [userId]
          : [],
      pay:
        selectedResponsible.id == ResponsibleType.PayrollResponsible
          ? [userId]
          : [],
      yearEnd:
        selectedResponsible.id == ResponsibleType.YearEndResponsible
          ? [userId]
          : [],
      charteredAccountant:
        selectedResponsible.id == ResponsibleType.CharteredAccountantResponsible
          ? [userId]
          : [],
      accountingBackup:
        selectedResponsible.id == ResponsibleType.AccountingBackupResponsible
          ? [userId]
          : [],
      accountingOnboarding:
        selectedResponsible.id ==
        ResponsibleType.AccountingOnboardingResponsible
          ? [userId]
          : [],
      payBackup:
        selectedResponsible.id == ResponsibleType.PayrollBackupResponsible
          ? [userId]
          : [],
      payOnboarding:
        selectedResponsible.id == ResponsibleType.PayrollOnboardingResponsible
          ? [userId]
          : []
    };
    return responsibleFilter;
  }
}

export interface ClientRelationState {
  organizationId: number;
  responsibleType: ResponsibleType;
  clientIds: number[];
}

export interface ResponsibleTypes {
  id: ResponsibleType;
  name: string;
}
